/**
 * ConnectStreamsContext
 * 
 * Thin orchestrator that composes the modular hooks into a single context provider.
 * This is the main entry point for Amazon Connect Streams functionality in the app.
 * 
 * Architecture:
 * - useConnectStreamsInit: Handles CCP initialization and authentication
 * - useAgentState: Manages agent state, status, and agent-level actions
 * - useContactState: Manages contact/call state and call-level actions
 */
import React, {
    createContext,
    useContext,
    useCallback,
    useRef,
    ReactNode,
    useState,
} from 'react';

import { useConnectStreamsInit } from '../../hooks/useConnectStreamsInit';
import { useConnectStream } from '../../hooks/useConnectStream';
import { useConnectTabElection } from '../../hooks/useConnectTabElection';
import { useAgentNetworkTelemetry } from '../../hooks/useAgentNetworkTelemetry';
import { isMobilePhone } from '../../Utils/isMobilePhone';
import toaster from '../../Utils/toaster';
import type { SyncedAgentState } from '../../hooks/useConnectTabElection';
import type { ConferenceParticipant, AudioDeviceOption, SoftphoneFailureNotice, MicPermissionState } from '../../hooks/useConnectStream';
import type { ContactInfo, AgentStatus, VideoCapabilities, ChimeMeetingInfo, VoiceEnhancementMode, CcpAuthState, NetworkConnectionHealth, MissedCallNotice } from '../../types/amazon-connect-streams';
import { useAuth } from '../modules/auth/core/Auth';

// Define minimal CallData interface to avoid circular dependency (or import from location)`
export interface ActiveCallData {
    callId: string;
    contactId?: string;
    callHeaderId: string;
    caller: string;
    language: string;
    channel: string;
    type: string;
    timestamp?: string;
    action?: string;
    // ... other fields as needed
}

// Context Type - Combines all hook outputs
interface AWSConnectContextType {
    // Initialization State
    isInitialized: boolean;
    isLoggedIn: boolean;
    ccpAuthState: CcpAuthState;
    networkConnection: NetworkConnectionHealth;
    browserOnline: boolean;
    networkReachable: boolean;
    lastMissedCall: MissedCallNotice | null;
    hasTwoActiveParticipants: boolean;
    isUnderSupervision: boolean;

    // Agent State
    agentName: string;
    agentEmail: string;
    agentStatus: AgentStatus;
    isAgentMuted: boolean;
    voiceEnhancementMode: VoiceEnhancementMode;
    microphoneDeviceId: string | null;
    speakerDeviceId: string | null;
    microphones: AudioDeviceOption[];
    speakers: AudioDeviceOption[];

    // Contact State
    contact: ContactInfo | null;
    isIncomingCall: boolean;
    isConnected: boolean;
    isConnectionFailed: boolean;
    callDuration: number;
    isCallOnHold: boolean;
    canTransferContact: boolean | null;
    softphoneFailure: SoftphoneFailureNotice | null;
    micPermission: MicPermissionState;
    networkDropNotice: boolean;

    // Active Call Data (Persisted from Modal)
    activeCallData: ActiveCallData | null;
    setActiveCallData: (data: ActiveCallData | null) => void;

    // Video
    videoCapabilities: VideoCapabilities | null;
    videoConnectionInfo: ChimeMeetingInfo | null;

    // Agent Actions
    setAgentAvailable: (options?: { force?: boolean }) => void;
    setAgentOffline: () => void;
    toggleAgentMute: () => void;
    logout: () => Promise<void>;
    setVoiceEnhancementMode: (mode: VoiceEnhancementMode) => Promise<void>;
    refreshAudioDevices: () => Promise<void>;
    setMicrophoneDevice: (deviceId: string) => Promise<void>;
    setSpeakerDevice: (deviceId: string) => Promise<void>;
    clearSoftphoneFailure: () => void;
    dismissConnectionFailure: () => void;
    ensureMicrophoneReady: () => Promise<boolean>;
    refreshMicPermission: () => Promise<MicPermissionState>;
    dismissNetworkDropNotice: () => void;

    // Contact Actions
    acceptCall: () => void;
    rejectCall: () => void;
    setShouldAutoAcceptCall: (value: boolean) => void;
    endCall: () => void;
    clearContact: () => void;
    toggleCallHold: () => void;
    dismissMissedCall: () => void;

    // Conference
    conferenceParticipants: ConferenceParticipant[];
    isAddingParticipant: boolean;
    addParticipant: (phoneNumber: string) => Promise<void>;
    refreshParticipants: () => void;
    conferenceAll: () => void;
    holdParticipant: (connectionId: string) => void;
    resumeParticipant: (connectionId: string) => void;
    disconnectParticipant: (connectionId: string) => void;
    sendDtmf: (digit: string) => void;
    dialableCountries: string[];

    // Auth Actions
    openLoginPopup: (onClose?: () => void) => void;
    reauthenticate: () => void;

    // Multi-tab coordination
    isMasterTab: boolean | null;
    syncedCallActive: boolean;
    syncedAgentState: SyncedAgentState;
    publishCallActive: (active: boolean) => void;
    publishAgentState: (state: SyncedAgentState) => void;
    requestMasterFocus: () => void;
}

const defaultContext: AWSConnectContextType = {
    // Initialization
    isInitialized: false,
    isLoggedIn: false,
    ccpAuthState: 'ccp_loading',
    networkConnection: { status: 'unknown', updatedAt: 0, websocketConnected: true },
    browserOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    networkReachable: true,
    lastMissedCall: null,
    hasTwoActiveParticipants: false,
    isUnderSupervision: false,

    // Agent
    agentName: '',
    agentEmail: '',
    agentStatus: 'Offline',
    isAgentMuted: false,
    voiceEnhancementMode: 'NONE',
    microphoneDeviceId: null,
    speakerDeviceId: null,
    microphones: [],
    speakers: [],

    // Contact
    contact: null,
    isIncomingCall: false,
    isConnected: false,
    isConnectionFailed: false,
    callDuration: 0,
    isCallOnHold: false,
    canTransferContact: null,
    softphoneFailure: null,
    micPermission: 'unknown',
    networkDropNotice: false,

    // Active Call Data
    activeCallData: null,
    setActiveCallData: () => { },

    // Video
    videoCapabilities: null,
    videoConnectionInfo: null,

    // Agent Actions
    setAgentAvailable: () => { },
    setAgentOffline: () => { },
    toggleAgentMute: () => { },
    logout: async () => { },
    setVoiceEnhancementMode: async () => { },
    refreshAudioDevices: async () => { },
    setMicrophoneDevice: async () => { },
    setSpeakerDevice: async () => { },
    clearSoftphoneFailure: () => { },
    dismissConnectionFailure: () => { },
    ensureMicrophoneReady: async () => false,
    refreshMicPermission: async () => 'unknown',
    dismissNetworkDropNotice: () => { },

    // Contact Actions
    acceptCall: () => { },
    rejectCall: () => { },
    setShouldAutoAcceptCall: () => { },
    endCall: () => { },
    clearContact: () => { },
    toggleCallHold: () => { },
    dismissMissedCall: () => { },

    // Conference
    conferenceParticipants: [],
    isAddingParticipant: false,
    addParticipant: async () => { },
    refreshParticipants: () => { },
    conferenceAll: () => { },
    holdParticipant: () => { },
    resumeParticipant: () => { },
    disconnectParticipant: () => { },
    sendDtmf: () => { },
    dialableCountries: [],

    // Auth Actions
    openLoginPopup: (_onClose?: () => void) => { },
    reauthenticate: () => { },

    // Multi-tab coordination
    isMasterTab: null,
    syncedCallActive: false,
    syncedAgentState: { isLoggedIn: false, agentStatus: 'Offline' },
    publishCallActive: () => { },
    publishAgentState: () => { },
    requestMasterFocus: () => { },
};

const AWSConnectContext = createContext<AWSConnectContextType>(defaultContext);

interface AWSConnectProviderProps {
    children: ReactNode;
}

export const ConnectStreamsProvider: React.FC<AWSConnectProviderProps> = ({
    children,
}) => {
    const { currentUser } = useAuth();
    const {
        isMasterTab,
        syncedCallActive,
        syncedAgentState,
        publishCallActive,
        publishAgentState,
        requestMasterFocus,
    } = useConnectTabElection();

    const sdkEnabled = isMasterTab === true && !isMobilePhone();

    // Initialize CCP and handle auth (master tab only)
    const {
        isInitialized,
        isLoggedIn,
        ccpAuthState,
        openLoginPopup,
        reauthenticate,
        setIsLoggedIn,
        setIsInitialized,
    } = useConnectStreamsInit({ enabled: sdkEnabled });

    const lastSoftphoneToastAtRef = useRef(0);

    // Agent state and actions
    const {
        // Agent State
        agentName,
        agentEmail,
        agentStatus,
        isAgentMuted,

        // Contact State
        contact,
        isIncomingCall,
        isConnected,
        isConnectionFailed,
        callDuration,
        isCallOnHold,

        // Actions
        setAgentAvailable,
        setAgentOffline,
        toggleAgentMute,
        logoutAgent,
        acceptCall,
        rejectCall,
        setShouldAutoAcceptCall,
        endCall,
        clearContact,
        toggleCallHold,
        dismissMissedCall,
        networkConnection,
        browserOnline,
        networkReachable,
        lastMissedCall,
        hasTwoActiveParticipants,
        isUnderSupervision,

        // Conference
        conferenceParticipants,
        isAddingParticipant,
        addParticipant,
        refreshParticipants,
        conferenceAll,
        holdParticipant,
        resumeParticipant,
        disconnectParticipant,
        sendDtmf,
        dialableCountries,

        // Video
        videoCapabilities,
        videoConnectionInfo,

        voiceEnhancementMode,
        setVoiceEnhancementMode,
        canTransferContact,
        microphoneDeviceId,
        speakerDeviceId,
        microphones,
        speakers,
        refreshAudioDevices,
        setMicrophoneDevice,
        setSpeakerDevice,
        softphoneFailure,
        micPermission,
        clearSoftphoneFailure,
        dismissConnectionFailure,
        ensureMicrophoneReady,
        refreshMicPermission,
        networkDropNotice,
        dismissNetworkDropNotice,
    } = useConnectStream({
        // Agent Events
        onAgentInitialized: () => {
            // Agent detected - user has active session
            // Set both initialized and logged in together
            setIsLoggedIn(true);
            setIsInitialized(true);
        },

        // Contact Events
        onCallEnded: () => {
            console.log('[ConnectStreamsProvider] Call ended - clearing active call data');
            setActiveCallData(null);
        },

        onSoftphoneFailure: (failure) => {
            if (failure.at === lastSoftphoneToastAtRef.current) return;
            lastSoftphoneToastAtRef.current = failure.at;
            toaster('error', failure.summary || failure.title || 'Connection failed');
        },

    });

    // Active Call Data Persistence
    const [activeCallData, setActiveCallData] = useState<ActiveCallData | null>(null);

    // Continuous network telemetry on InterpreterHub → Influx (master tab + CCP logged in only).
    useAgentNetworkTelemetry({
        isMasterTab,
        isLoggedIn,
        agentStatus: typeof agentStatus === 'string' ? agentStatus : String(agentStatus || 'Offline'),
        contactId: contact?.contactId || activeCallData?.contactId || null,
        muted: isAgentMuted,
    });

    // Wrap logout to reset contact state as well. Returns the underlying Promise
    // so callers can sequence post-logout work (e.g. page refresh, portal logout).
    const logout = useCallback(() => logoutAgent(), [logoutAgent]);

    // Compose context value
    const value: AWSConnectContextType = {
        // Initialization
        isInitialized,
        isLoggedIn,
        ccpAuthState,
        networkConnection,
        browserOnline,
        networkReachable,
        lastMissedCall,
        hasTwoActiveParticipants,
        isUnderSupervision,

        // Agent State
        agentName,
        agentEmail,
        agentStatus,
        isAgentMuted,
        voiceEnhancementMode,
        microphoneDeviceId,
        speakerDeviceId,
        microphones,
        speakers,

        // Contact State
        contact,
        isIncomingCall,
        isConnected,
        isConnectionFailed,
        callDuration,
        isCallOnHold,
        canTransferContact,
        softphoneFailure,
        micPermission,

        // Active Call Data
        activeCallData,
        setActiveCallData,

        // Agent Actions
        setAgentAvailable,
        setAgentOffline,
        toggleAgentMute,
        logout,
        setVoiceEnhancementMode,
        refreshAudioDevices,
        setMicrophoneDevice,
        setSpeakerDevice,
        clearSoftphoneFailure,
        dismissConnectionFailure,
        ensureMicrophoneReady,
        refreshMicPermission,
        dismissNetworkDropNotice,
        networkDropNotice,

        // Contact Actions
        acceptCall,
        rejectCall,
        setShouldAutoAcceptCall,

        endCall,
        clearContact,
        toggleCallHold,
        dismissMissedCall,

        // Conference
        conferenceParticipants,
        isAddingParticipant,
        addParticipant,
        refreshParticipants,
        conferenceAll,
        holdParticipant,
        resumeParticipant,
        disconnectParticipant,
        sendDtmf,
        dialableCountries,

        // Auth Actions
        openLoginPopup,
        reauthenticate,

        // Video
        videoCapabilities,
        videoConnectionInfo,

        // Multi-tab coordination
        isMasterTab,
        syncedCallActive,
        syncedAgentState,
        publishCallActive,
        publishAgentState,
        requestMasterFocus,
    };

    return (
        <AWSConnectContext.Provider value={value}>
            {children}
        </AWSConnectContext.Provider>
    );
};

export const useAWSConnect = (): AWSConnectContextType => {
    const context = useContext(AWSConnectContext);
    if (!context) {
        throw new Error('useAWSConnect must be used within an AWSConnectProvider');
    }
    return context;
};

export default AWSConnectContext;
